import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import CountrySelect from './CountrySelect';
import { getCountryByCode, getDefaultCountry } from '@/data/countries';
import { parsePhone, formatE164 } from '@/lib/phone';

interface PhoneInputProps {
  /** E.164 formatted value (e.g., '+48123456789') */
  value: string;
  /** Called with E.164 formatted value */
  onChange: (value: string) => void;
  /** Default country code if value is empty (ISO code) */
  defaultCountry?: string;
  /** Field is required */
  required?: boolean;
  /** Disable input */
  disabled?: boolean;
  /** Placeholder for national number */
  placeholder?: string;
  /** Custom class name for container */
  className?: string;
}

export default function PhoneInput({
  value,
  onChange,
  defaultCountry,
  required = false,
  disabled = false,
  placeholder,
  className = '',
}: PhoneInputProps) {
  const { t } = useTranslation();

  // Internal state for country and national number
  const [countryCode, setCountryCode] = useState<string>(() => {
    // Try to parse existing value first
    if (value) {
      const parsed = parsePhone(value);
      if (parsed) return parsed.countryCode;
    }
    // Fall back to default country
    if (defaultCountry) {
      const country = getCountryByCode(defaultCountry);
      if (country) return country.code;
    }
    return getDefaultCountry().code;
  });

  const [nationalNumber, setNationalNumber] = useState<string>(() => {
    if (value) {
      const parsed = parsePhone(value);
      if (parsed) return parsed.nationalNumber;
    }
    return '';
  });

  // Sync internal state when external value changes
  useEffect(() => {
    if (value) {
      const parsed = parsePhone(value);
      if (parsed) {
        setCountryCode(parsed.countryCode);
        setNationalNumber(parsed.nationalNumber);
        return;
      }
    }
    // If value is empty or couldn't be parsed, keep current country but clear number
    if (!value) {
      setNationalNumber('');
    }
  }, [value]);

  // Emit combined value when country or number changes
  const handleCountryChange = (newCountryCode: string) => {
    setCountryCode(newCountryCode);
    const country = getCountryByCode(newCountryCode);
    if (country && nationalNumber) {
      const combined = formatE164(country.dialCode, nationalNumber);
      onChange(combined);
    } else if (!nationalNumber) {
      onChange('');
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow digits, spaces, and dashes
    const rawValue = e.target.value;
    const cleanValue = rawValue.replace(/[^\d\s-]/g, '');
    const digitsOnly = cleanValue.replace(/\D/g, '');

    setNationalNumber(digitsOnly);

    const country = getCountryByCode(countryCode);
    if (country && digitsOnly) {
      const combined = formatE164(country.dialCode, digitsOnly);
      onChange(combined);
    } else {
      onChange('');
    }
  };

  return (
    <div className={`flex ${className}`}>
      <CountrySelect
        value={countryCode}
        onChange={handleCountryChange}
        displayMode="dialCode"
        disabled={disabled}
        showBorder={true}
        borderRadius="left"
        className="flex-shrink-0"
      />
      <input
        type="tel"
        inputMode="tel"
        value={nationalNumber}
        onChange={handleNumberChange}
        placeholder={placeholder || t('dashboard.form.phoneNumber', 'Phone number')}
        required={required}
        disabled={disabled}
        className="flex-1 px-4 py-3 bg-white/50 border border-l-0 border-gray-200 rounded-r-xl focus:ring-2 focus:ring-mk-pink-500 focus:border-transparent focus:bg-white transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
        aria-label={t('dashboard.form.phoneNumber', 'Phone number')}
      />
    </div>
  );
}
